import { Controller } from "@hotwired/stimulus"

// Phase 3.7 Tier 4 Step 39 — sandbox lifecycle Stimulus controller.
//
// Mixpanel events for this surface fire server-side (controller +
// ProvisionSandboxJob) — same pattern Tier 3 established for partner
// API key lifecycle. Client-side tracking would double-count and
// might fire even when the server-side write fails, so this file
// stays Mixpanel-free.
//
// Two long-running flows live here:
//
//   1. Create — open the modal, POST to /partners/sandboxes, swap the
//      modal into a "Provisioning…" spinner, then poll the sandbox's
//      status endpoint every 3s until state === "ready" (or "failed"),
//      then redirect to the show page.
//
//   2. Per-card actions (Reset / Extend / Delete / Open delete modal) —
//      single-shot POSTs against the sandbox's member route, with a
//      toast on success and a flash-and-reload to refresh card chrome
//      (state chips, days-remaining, extension counts).
//
// Both flows share `_csrfToken()` + `_postJson()` helpers. Toasts use
// the `flash-toast-notice` / `flash-toast-alert` shape per CLAUDE.md.
export default class extends Controller {
  static targets = [
    "modal",
    "formStep",
    "progressStep",
    "errorStep",
    "nameInput",
    "progressName",
    "errorMessage",
    "deleteModal",
    "deleteName",
    "card"
  ]

  static values = {
    listUrl: String,
    pollInterval: { type: Number, default: 3000 }
  }

  connect() {
    this._pollTimer = null
    this._activeSandboxId = null
    this._pendingDeleteId = null
  }

  disconnect() {
    this._stopPolling()
  }

  // ── Create flow ───────────────────────────────────────────────

  openCreate() {
    this._showFormStep()
    if (this.hasNameInputTarget) {
      this.nameInputTarget.value = ""
    }
    this._showModal(this.modalTarget)
    if (this.hasNameInputTarget) {
      requestAnimationFrame(() => this.nameInputTarget.focus())
    }
  }

  closeCreate() {
    this._hideModal(this.modalTarget)
    this._stopPolling()
  }

  closeOnBackdrop(event) {
    if (event.target === this.modalTarget) this.closeCreate()
  }

  closeDeleteOnBackdrop(event) {
    if (event.target === this.deleteModalTarget) this.closeDelete()
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  async submitCreate() {
    const name = (this.nameInputTarget.value || "").trim()
    const payload = name ? { sandbox: { name } } : {}
    try {
      const response = await this._postJson(this.listUrlValue, payload)
      if (!response.ok) {
        const body = await response.json().catch(() => ({}))
        this._showErrorStep(body.error || "Could not create sandbox.")
        return
      }
      const data = await response.json()
      this._activeSandboxId = data.id
      this._showProgressStep(data.name || name)
      this._startPolling(data)
    } catch (e) {
      this._showErrorStep(e.message || "Network error.")
    }
  }

  // ── Per-card actions ───────────────────────────────────────────

  async reset(event) {
    const id = event.currentTarget.dataset.sandboxId
    const url = `${this.listUrlValue}/${id}/reset`
    const response = await this._postJson(url, {})
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      return this._showToast(body.error || "Could not reset sandbox.", "alert")
    }
    this._showToast("Sandbox data reset to canonical fixture.", "notice")
    this._reloadAfterTick()
  }

  async extend(event) {
    const id = event.currentTarget.dataset.sandboxId
    const url = `${this.listUrlValue}/${id}/extend`
    const response = await this._postJson(url, {})
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      return this._showToast(body.error || "Could not extend sandbox.", "alert")
    }
    const data = await response.json()
    this._showToast(`Extended — now ${data.days_remaining} days remaining.`, "notice")
    this._reloadAfterTick()
  }

  openDelete(event) {
    this._pendingDeleteId = event.currentTarget.dataset.sandboxId
    if (this.hasDeleteNameTarget) {
      this.deleteNameTarget.textContent = event.currentTarget.dataset.sandboxName || "this sandbox"
    }
    this._showModal(this.deleteModalTarget)
  }

  closeDelete() {
    this._hideModal(this.deleteModalTarget)
    this._pendingDeleteId = null
  }

  async confirmDelete() {
    if (!this._pendingDeleteId) return this.closeDelete()
    const url = `${this.listUrlValue}/${this._pendingDeleteId}`
    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: { "X-CSRF-Token": this._csrfToken(), "Accept": "application/json" },
        credentials: "same-origin"
      })
      if (!response.ok) {
        const body = await response.json().catch(() => ({}))
        return this._showToast(body.error || "Could not delete sandbox.", "alert")
      }
      this._showToast("Sandbox deleted.", "notice")
      this._reloadAfterTick()
    } finally {
      this.closeDelete()
    }
  }

  // ── Polling ────────────────────────────────────────────────────

  _startPolling(initialPayload) {
    if (initialPayload && initialPayload.state === "ready") {
      this._handleReady(initialPayload)
      return
    }
    this._stopPolling()
    this._pollTimer = setInterval(() => this._pollOnce(), this.pollIntervalValue)
  }

  _stopPolling() {
    if (this._pollTimer) {
      clearInterval(this._pollTimer)
      this._pollTimer = null
    }
  }

  async _pollOnce() {
    if (!this._activeSandboxId) return this._stopPolling()
    try {
      const response = await fetch(`${this.listUrlValue}/${this._activeSandboxId}/status`, {
        headers: { "Accept": "application/json" },
        credentials: "same-origin"
      })
      if (!response.ok) return
      const data = await response.json()
      if (data.state === "ready") {
        this._handleReady(data)
      } else if (data.state === "failed") {
        this._stopPolling()
        this._showErrorStep(data.error_message || "Provisioning failed.")
      }
    } catch (_e) {
      // transient network error — let the next tick retry
    }
  }

  _handleReady(data) {
    this._stopPolling()
    window.location.assign(data.show_url || `${this.listUrlValue}/${data.id}`)
  }

  // ── Modal step swaps ───────────────────────────────────────────

  _showFormStep() {
    this.formStepTarget.classList.remove("hidden")
    this.progressStepTarget.classList.add("hidden")
    this.errorStepTarget.classList.add("hidden")
  }

  _showProgressStep(name) {
    this.formStepTarget.classList.add("hidden")
    this.progressStepTarget.classList.remove("hidden")
    this.errorStepTarget.classList.add("hidden")
    if (name && this.hasProgressNameTarget) {
      this.progressNameTarget.textContent = `"${name}"`
    }
  }

  _showErrorStep(message) {
    this.formStepTarget.classList.add("hidden")
    this.progressStepTarget.classList.add("hidden")
    this.errorStepTarget.classList.remove("hidden")
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
    }
  }

  // ── Modal show/hide helpers ────────────────────────────────────

  _showModal(modal) {
    modal.classList.remove("hidden")
    document.body.style.overflow = "hidden"
  }

  _hideModal(modal) {
    modal.classList.add("hidden")
    document.body.style.overflow = ""
  }

  // ── Toast + reload ─────────────────────────────────────────────

  _showToast(message, type) {
    const cls = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    const toast = document.createElement("div")
    toast.className = cls
    toast.setAttribute("data-controller", "flash")
    toast.setAttribute("data-flash-dismiss-after-value", "5000")
    toast.textContent = message
    document.body.prepend(toast)
  }

  _reloadAfterTick() {
    setTimeout(() => window.location.reload(), 350)
  }

  // ── Helpers ────────────────────────────────────────────────────

  _csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta ? meta.getAttribute("content") : ""
  }

  async _postJson(url, body) {
    return fetch(url, {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": this._csrfToken()
      },
      credentials: "same-origin",
      body: JSON.stringify(body || {})
    })
  }
}
